import React, { useState, useEffect, createContext, useContext } from 'react';
import { View, StyleSheet, Dimensions, Platform } from 'react-native';

interface MobileContainerProps {
  children: React.ReactNode;
}

// Context to track if we're inside a mobile container
const MobileContainerContext = createContext<{ isInMobileContainer: boolean; isDesktop: boolean }>({
  isInMobileContainer: false,
  isDesktop: false,
});

export const useMobileContainer = () => useContext(MobileContainerContext);

/**
 * MobileContainer wraps content in a phone-sized container on desktop screens
 * with a gradient backdrop, while remaining full-width on mobile devices.
 * 
 * Desktop (≥768px): 430px centered container with gradient backdrop
 * Mobile (<768px): Full-width, no container effect
 */
const MobileContainer: React.FC<MobileContainerProps> = ({ children }) => {
  const [windowWidth, setWindowWidth] = useState(
    Platform.OS === 'web' ? window.innerWidth : Dimensions.get('window').width
  );

  useEffect(() => {
    if (Platform.OS !== 'web') {
      // On native, use Dimensions API
      const subscription = Dimensions.addEventListener('change', ({ window }) => {
        setWindowWidth(window.width);
      });

      return () => {
        if (subscription?.remove) {
          subscription.remove();
        }
      };
    } else {
      // On web, use window resize listener
      const handleResize = () => {
        setWindowWidth(window.innerWidth);
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  const isDesktop = windowWidth >= 768;

  if (!isDesktop) {
    // On mobile, render children without container
    return (
      <MobileContainerContext.Provider value={{ isInMobileContainer: true, isDesktop: false }}>
        {children}
      </MobileContainerContext.Provider>
    );
  }

  // On desktop, render with backdrop and centered container
  return (
    <MobileContainerContext.Provider value={{ isInMobileContainer: true, isDesktop: true }}>
      <View style={styles.backdrop}>
        <View style={styles.container}>
          {children}
        </View>
      </View>
    </MobileContainerContext.Provider>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: '#f5f7fa',
    ...(Platform.OS === 'web' && {
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    }),
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  container: {
    flex: 1,
    width: '100%',
    maxWidth: 600,
    backgroundColor: '#ffffff',
    minHeight: Platform.OS === 'web' ? 'var(--app-height)' as any : '100%',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    }),
    position: 'relative',
  },
});

export default MobileContainer;
